import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAdmin } from "../../../contexts/AdminContext";

const tabImages: Record<"exterieur" | "interieur" | "terrain", string> = {
  exterieur:
    "https://readdy.ai/api/search-image?query=stunning%20modern%20luxury%20white%20villa%20exterior%20with%20large%20infinity%20swimming%20pool%2C%20manicured%20tropical%20garden%2C%20palm%20trees%2C%20french%20riviera%2C%20golden%20hour%20sunlight%2C%20architectural%20photography%2C%20ultra%20sharp%2C%20high-end%20real%20estate%2C%20minimal%20clean%20sky%20background%2C%20cinematic&width=900&height=700&seq=prop-ext-1&orientation=landscape",
  interieur:
    "https://readdy.ai/api/search-image?query=breathtaking%20luxury%20villa%20interior%20open%20plan%20living%20room%20with%20floor%20to%20ceiling%20windows%2C%20panoramic%20sea%20view%2C%20designer%20furniture%2C%20marble%20floors%2C%20elegant%20minimal%20style%2C%20natural%20light%2C%20high-end%20real%20estate%20photography%2C%20ultra%20sharp%2C%20no%20people&width=900&height=700&seq=prop-int-1&orientation=landscape",
  terrain:
    "https://readdy.ai/api/search-image?query=aerial%20drone%20view%20luxury%20villa%20large%20landscaped%20garden%20with%20pool%20and%20terrace%2C%20green%20lawn%2C%20mediterranean%20vegetation%2C%20french%20riviera%20hills%2C%20beautiful%20estate%20property%2C%20golden%20afternoon%20light%2C%20real%20estate%20photography&width=900&height=700&seq=prop-terrain-1&orientation=landscape",
};

export default function PropertyShowcase() {
  const { settings } = useAdmin();
  const [visible, setVisible] = useState(false);
  const [tab, setTab] = useState<"exterieur" | "interieur" | "terrain">("exterieur");
  const ref = useRef<HTMLDivElement>(null);

  const propDetails = [
    { icon: "ri-ruler-2-line", value: `${settings.propertySurface} m²`, label: "Fläche", color: "text-primary", bg: "bg-amber-50" },
    { icon: "ri-hotel-bed-line", value: settings.propertyRooms, label: "Schlafzimmer", color: "text-sky-500", bg: "bg-sky-50" },
    { icon: "ri-shower-line", value: settings.propertyBathrooms, label: "Badezimmer", color: "text-teal-500", bg: "bg-teal-50" },
    { icon: "ri-drop-line", value: "Privat & Spa", label: "Pool", color: "text-secondary", bg: "bg-emerald-50" },
    { icon: "ri-tree-line", value: `${settings.propertyLand} m²`, label: "Garten", color: "text-lime-600", bg: "bg-lime-50" },
    { icon: "ri-car-line", value: "2 Fahrzeuge", label: "Garage", color: "text-primary", bg: "bg-orange-50" },
    { icon: "ri-eye-2-line", value: "Panorama", label: "Meerblick", color: "text-blue-500", bg: "bg-blue-50" },
    { icon: "ri-leaf-line", value: "Klasse A+", label: "Effizienz", color: "text-secondary", bg: "bg-emerald-50" },
    { icon: "ri-sparkling-2-line", value: "Neu (2021)", label: "Zustand", color: "text-primary", bg: "bg-amber-50" },
    { icon: "ri-armchair-line", value: "Luxus & Design", label: "Möbliert", color: "text-violet-500", bg: "bg-violet-50" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <section ref={ref} className="py-24 bg-slate-50 overflow-hidden relative">
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-checkered opacity-50 pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">

        {/* Header */}
        <div className={`mb-16 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="flex items-center gap-2 mb-4">
            <span className="h-px w-8 bg-primary"></span>
            <span className="uppercase text-primary text-[10px] font-black tracking-[0.2em]">
              Einzigartige Ziehung {settings.drawId}
            </span>
          </div>
          
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <h2 className="font-playfair text-4xl md:text-6xl font-black text-slate-900 leading-tight">
              Die Prestigevilla, die<br />
              <span className="gradient-text italic font-medium">Ihr Schicksal verändern wird</span>
            </h2>
            <div className="lg:text-right">
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Wert der Ausstattung</p>
              <p className="font-playfair text-5xl md:text-6xl font-black text-slate-900 tracking-tight">
                {settings.lotteryValue}
              </p>
            </div>
          </div>
        </div>

        {/* Main layout */}
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch transition-all duration-1000 delay-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>

          {/* IMAGE — left column (7/12) */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            
            <div className="relative rounded-[2.5rem] overflow-hidden group shadow-2xl min-h-[450px] lg:min-h-[600px] border-8 border-white">
              {[
                { id: "exterieur", src: settings.propertyImages.showcaseExterior },
                { id: "interieur", src: settings.propertyImages.showcaseInterior },
                { id: "terrain", src: settings.propertyImages.showcaseLand },
              ].map((item) => (
                <img
                  key={item.id}
                  src={item.src}
                  alt={`Villa — ${item.id}`}
                  className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-110 transition-all duration-[2000ms] ease-out"
                  style={{ opacity: tab === item.id ? 1 : 0, transform: tab === item.id ? "scale(1)" : "scale(1.1)" }}
                />
              ))}

              {/* Overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-slate-900/30" />

              {/* Tab switcher - Glass style */}
              <div className="absolute bottom-8 left-8 flex gap-3 z-10">
                {(["exterieur", "interieur", "terrain"] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-5 py-2 rounded-full text-[10px] uppercase font-black tracking-widest cursor-pointer transition-all duration-300 shadow-lg ${
                      tab === t
                        ? "bg-white text-slate-900 scale-105"
                        : "bg-black/20 text-white hover:bg-black/40 backdrop-blur-md border border-white/10"
                    }`}
                  >
                    {t === 'exterieur' ? 'Außenansicht' : t === 'interieur' ? 'Wohnbereiche' : 'Gärten & Terrassen'}
                  </button>
                ))}
              </div>

              {/* Badge Corner */}
              <div className="absolute top-8 left-8 bg-primary-light text-slate-900 text-[10px] font-black px-4 py-2 rounded-full shadow-2xl z-10 flex items-center gap-2">
                <i className="ri-medal-fill text-sm"></i> ZERTIFIZIERTES ANGEBOT
              </div>
            </div>
          </div>

          {/* DETAILS — right column (5/12) */}
          <div className="lg:col-span-5 flex flex-col gap-8">
            
            <div className="bg-white rounded-[2.5rem] p-8 lg:p-10 border border-slate-100 shadow-2xl flex-1 flex flex-col relative overflow-hidden">
              {/* Corner decor */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-full -mr-16 -mt-16 blur-3xl opacity-50" />
              
              <div className="relative z-10">
                <div className="mb-10 pb-4 border-b border-slate-100">
                  <h3 className="text-xl font-bold font-inter text-slate-900 tracking-tight">Immobilienmerkmale</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-8 gap-x-12">
                  {propDetails.map((detail) => (
                    <div key={detail.label} className="flex items-center gap-4 group">
                      <div className="flex-shrink-0 w-6 flex justify-center">
                        <i className={`${detail.icon} text-lg text-slate-300 group-hover:text-primary transition-colors duration-300`}></i>
                      </div>
                      <div className="flex flex-col">
                        <p className="text-slate-400 text-[9px] font-bold uppercase tracking-widest leading-none mb-1.5">{detail.label}</p>
                        <p className="font-inter text-base font-bold text-slate-900 leading-none">{detail.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-12 pt-8 border-t border-slate-100">
                  <Link
                    to="/forfaits"
                    className="group flex items-center justify-center gap-3 bg-slate-900 text-white font-black py-5 rounded-[2rem] transition-all duration-500 hover:bg-primary hover:scale-[1.02] shadow-xl hover:shadow-primary/25"
                  >
                    MEINE LOSE KAUFEN
                    <i className="ri-arrow-right-up-line text-lg group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
